/**
 * LiveChatWidget — the site-wide floating live-chat launcher in the bottom-right
 * corner. Available to every visitor (logged-in or anonymous).
 *
 * Behaviour the storefront asks for:
 *  - Opens to a friendly greeting + a short pre-chat form ("what do you need
 *    help with") so the first message has context.
 *  - "Snap off" (–) minimizes the panel back to the launcher bubble; "Hide" (×)
 *    dismisses the launcher entirely for the rest of the browser session.
 *  - Reassures the visitor a site admin will get to them shortly.
 *  - Persists the conversation across reloads via a random per-browser token,
 *    and polls for staff replies (unread badge on the launcher).
 *
 * Design notes:
 *  - Uses plain fetch (not the axios client) so it stays fully decoupled from
 *    the auth interceptor — this runs for logged-out visitors too.
 *  - Hidden on /admin so it never overlays the dashboard.
 */
import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

const API = (process.env.REACT_APP_API_URL || '/api') + '/support';
const TOKEN_KEY = 'aloha-chat-token';
const ID_KEY    = 'aloha-chat-id';
const POLL_MS   = 5000;

const ls = {
  get: (k) => { try { return localStorage.getItem(k); } catch { return null; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch {} },
  del: (k) => { try { localStorage.removeItem(k); } catch {} },
};

export default function LiveChatWidget() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/admin');

  const [open, setOpen] = useState(false);
  const [chatId, setChatId] = useState(() => ls.get(ID_KEY));
  const [token, setToken]   = useState(() => ls.get(TOKEN_KEY));
  const [messages, setMessages] = useState([]);
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  // Pre-chat form (shown until a conversation exists).
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [topic, setTopic] = useState('');
  const [firstMsg, setFirstMsg] = useState('');

  const scrollerRef = useRef(null);
  const seenAdminRef = useRef(0); // admin messages acknowledged the last time panel was open

  const started = !!chatId && !!token;

  // ── poll the thread while a chat exists ──────────────────────────────────────
  const load = async () => {
    if (!chatId || !token) return;
    try {
      const r = await fetch(`${API}/${chatId}?token=${encodeURIComponent(token)}`, { credentials: 'include' });
      if (r.status === 404) { // chat was deleted server-side — reset to a fresh form
        ls.del(ID_KEY); ls.del(TOKEN_KEY);
        setChatId(null); setToken(null); setMessages([]);
        return;
      }
      const d = await r.json();
      setMessages(Array.isArray(d.messages) ? d.messages : []);
    } catch {
      // silent — transient poll failures shouldn't spam the visitor
    }
  };

  useEffect(() => {
    if (!started || isAdmin) return undefined;
    load();
    const id = setInterval(load, POLL_MS);
    return () => clearInterval(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, isAdmin, chatId, token]);

  // Auto-scroll to newest when open.
  useEffect(() => {
    if (open && scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages.length, open]);

  // Track unread admin replies for the launcher badge.
  const adminCount = messages.filter(m => m.senderType === 'admin').length;
  useEffect(() => { if (open) seenAdminRef.current = adminCount; }, [open, adminCount]);
  const unread = open ? 0 : Math.max(0, adminCount - seenAdminRef.current);

  // ── actions ──────────────────────────────────────────────────────────────────
  const startChat = async () => {
    const msg = firstMsg.trim();
    if (!msg || sending) return;
    setSending(true); setError('');
    try {
      const r = await fetch(`${API}/start`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name: name.trim() || undefined,
          email: email.trim() || undefined,
          topic: topic.trim() || undefined,
          message: msg,
          pageUrl: window.location.pathname,
        }),
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      ls.set(ID_KEY, d.chat.id); ls.set(TOKEN_KEY, d.chat.token);
      setChatId(d.chat.id); setToken(d.chat.token);
      setMessages(d.messages || []);
      setFirstMsg('');
    } catch {
      setError('Could not start the chat. Please try again.');
    } finally {
      setSending(false);
    }
  };

  const send = async () => {
    const text = draft.trim();
    if (!text || sending) return;
    setSending(true); setError('');
    // optimistic append
    const optimistic = { id: `tmp-${Date.now()}`, senderType: 'visitor', body: text, createdAt: new Date().toISOString() };
    setMessages(m => [...m, optimistic]);
    setDraft('');
    try {
      const r = await fetch(`${API}/${chatId}/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ token, body: text }),
      });
      if (!r.ok) throw new Error();
      const saved = await r.json();
      setMessages(m => m.map(x => (x.id === optimistic.id ? saved : x)));
    } catch {
      setMessages(m => m.filter(x => x.id !== optimistic.id));
      setDraft(text);
      setError('Message failed to send.');
    } finally {
      setSending(false);
    }
  };

  const endChat = async () => {
    if (chatId && token) {
      try {
        await fetch(`${API}/${chatId}/close`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ token }),
        });
      } catch {}
    }
    ls.del(ID_KEY); ls.del(TOKEN_KEY);
    setChatId(null); setToken(null); setMessages([]);
    setOpen(false);
  };

  const onKeyDown = (handler) => (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handler(); }
  };

  if (isAdmin) return null;

  const senderLabel = (m) =>
    m.senderType === 'admin' ? 'Aloha Amino Team' :
    m.senderType === 'system' ? '' : 'You';

  return (
    <div className="fixed bottom-5 right-5 z-[9990] flex flex-col items-end">
      {/* Panel */}
      {open && (
        <div className="mb-3 w-[22rem] max-w-[calc(100vw-2.5rem)] rounded-2xl shadow-2xl overflow-hidden flex flex-col bg-white dark:bg-gray-900 ring-1 ring-black/5 border border-gray-100 dark:border-gray-700"
             style={{ height: '32rem' }}>
          {/* Header */}
          <div className="px-4 py-3 flex items-center justify-between bg-gradient-to-br from-tea-600 to-tea-500 text-white">
            <div className="min-w-0">
              <div className="font-bold text-sm leading-tight">Aloha Amino Support</div>
              <div className="text-[11px] mt-0.5 flex items-center gap-1.5 text-white/80">
                <span className="inline-block w-2 h-2 rounded-full bg-green-300 animate-pulse" />
                We typically reply in a few minutes
              </div>
            </div>
            {/* Minimize back to the launcher bubble — the launcher always stays
                visible so the chat can never get "lost". */}
            <button onClick={() => setOpen(false)} title="Minimize"
              className="shrink-0 w-8 h-8 flex items-center justify-center rounded-full hover:bg-white/15"
              aria-label="Minimize chat">
              <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
          </div>

          {/* Body */}
          {!started ? (
            // ── Pre-chat form ──────────────────────────────────────────────────
            <div className="flex-1 overflow-y-auto p-4">
              <div className="rounded-xl bg-tea-50 dark:bg-gray-800 border border-tea-100 dark:border-gray-700 p-3 text-sm text-gray-700 dark:text-gray-200 mb-4">
                <span className="text-lg mr-1">🌺</span>
                Aloha! Tell us what you need help with and a site admin will get to you shortly.
              </div>
              <div className="space-y-2.5">
                <input
                  value={name} onChange={e => setName(e.target.value)} maxLength={120}
                  placeholder="Your name (optional)"
                  className="w-full rounded-lg text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-tea-400"
                />
                <input
                  value={email} onChange={e => setEmail(e.target.value)} maxLength={160} type="email"
                  placeholder="Email (optional — so we can follow up)"
                  className="w-full rounded-lg text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-tea-400"
                />
                <input
                  value={topic} onChange={e => setTopic(e.target.value)} maxLength={200}
                  placeholder="Topic (e.g. Order status, Product question)"
                  className="w-full rounded-lg text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-tea-400"
                />
                <textarea
                  value={firstMsg} onChange={e => setFirstMsg(e.target.value)} onKeyDown={onKeyDown(startChat)}
                  rows={3} maxLength={4000}
                  placeholder="How can we help you?"
                  className="w-full resize-none rounded-lg text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-tea-400"
                />
                {error && <div className="text-xs text-rose-500">{error}</div>}
                <button
                  onClick={startChat} disabled={!firstMsg.trim() || sending}
                  className="w-full py-2.5 rounded-lg text-sm font-bold text-white bg-tea-600 hover:bg-tea-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                >
                  {sending ? 'Starting…' : 'Start chat'}
                </button>
              </div>
            </div>
          ) : (
            // ── Conversation ───────────────────────────────────────────────────
            <>
              <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-2 bg-gray-50 dark:bg-gray-950">
                {messages.map(m => {
                  if (m.senderType === 'system') {
                    return (
                      <div key={m.id} className="mx-auto max-w-[90%] text-center text-xs text-gray-500 dark:text-gray-400 bg-white dark:bg-gray-800 rounded-xl px-3 py-2 border border-gray-100 dark:border-gray-700">
                        {m.body}
                      </div>
                    );
                  }
                  const fromYou = m.senderType === 'visitor';
                  return (
                    <div key={m.id} className={'flex ' + (fromYou ? 'justify-end' : 'justify-start')}>
                      <div className={'max-w-[85%] rounded-2xl px-3 py-2 ' +
                        (fromYou
                          ? 'bg-tea-600 text-white rounded-br-sm'
                          : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-700 rounded-bl-sm')}>
                        {!fromYou && (
                          <div className="text-[10px] uppercase tracking-wide font-bold mb-0.5 text-tea-600 dark:text-tea-300">
                            {senderLabel(m)}
                          </div>
                        )}
                        <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                        <div className={'text-[9px] mt-1 ' + (fromYou ? 'text-white/70' : 'text-gray-400')}>
                          {new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Composer */}
              <div className="p-3 bg-white dark:bg-gray-900 border-t border-gray-100 dark:border-gray-700">
                {error && <div className="text-xs text-rose-500 mb-1.5">{error}</div>}
                <div className="flex items-end gap-2">
                  <textarea
                    rows={2} value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={onKeyDown(send)}
                    placeholder="Type a message…" maxLength={4000}
                    className="flex-1 resize-none rounded-lg text-sm px-3 py-2 border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-800 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-tea-400"
                  />
                  <button
                    onClick={send} disabled={!draft.trim() || sending}
                    className="shrink-0 px-4 py-2 rounded-lg text-sm font-bold text-white bg-tea-600 hover:bg-tea-700 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >Send</button>
                </div>
                <button onClick={endChat} className="mt-2 text-[11px] text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
                  End chat
                </button>
              </div>
            </>
          )}
        </div>
      )}

      {/* Launcher */}
      <button
        onClick={() => setOpen(o => !o)}
        className="relative w-14 h-14 rounded-full shadow-2xl flex items-center justify-center hover:scale-105 active:scale-95 transition-transform bg-gradient-to-br from-tea-600 to-tea-500 text-white"
        aria-label={open ? 'Minimize chat' : 'Open live chat'}
      >
        {open ? (
          <svg viewBox="0 0 24 24" className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        ) : (
          <svg viewBox="0 0 24 24" className="w-7 h-7" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 11.5a8.38 8.38 0 0 1-8.5 8.5 8.5 8.5 0 0 1-3.8-.9L3 20l1.4-4.2A8.4 8.4 0 0 1 3.5 11.5 8.38 8.38 0 0 1 12 3a8.38 8.38 0 0 1 9 8.5Z" />
          </svg>
        )}
        {unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[20px] h-5 px-1 rounded-full bg-rose-600 text-white text-[11px] font-bold flex items-center justify-center ring-2 ring-white">
            {unread}
          </span>
        )}
      </button>
    </div>
  );
}
